import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@1trading/react/components/ui/form';
import { RadioGroup, RadioGroupItem, RadioGroupItemCard } from '@1trading/react/components/ui/radio-group';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useGuardedFormStepper } from '@/components/ui/guarded-form-stepper';
import { PageHeading, PageHeadingDescription, PageHeadingTitle } from '@/components/ui/page-heading';
import type { AppropriatenessTestLeverageAttitude } from '@/features/futures/schemas/appropriateness-test';
import { usePromise } from '@/hooks/use-promise';
import { FUTURES_APPROPRIATENESS_LEVERAGE_ATTITUDE_ANSWERS } from '@/services/api/profile/futures-appropriateness';

const FuturesAppropriatenessTestFieldsetLeverageAttitudeLegend = () => {
  const { t } = useTranslation();

  return (
    <PageHeading asChild>
      <legend>
        <PageHeadingTitle>{t('FuturesAppropriatenessAssessment.leverageAttitude.title')}</PageHeadingTitle>
        <PageHeadingDescription>{t('FuturesAppropriatenessAssessment.leverageAttitude.description')}</PageHeadingDescription>
      </legend>
    </PageHeading>
  );
};
FuturesAppropriatenessTestFieldsetLeverageAttitudeLegend.displayName = 'FuturesAppropriatenessTestFieldsetLeverageAttitudeLegend';

const FuturesAppropriatenessTestFieldsetLeverageAttitude = () => {
  const form = useFormContext<Partial<AppropriatenessTestLeverageAttitude>>();
  const { nextStepDeferred } = useGuardedFormStepper();
  const { isPending, handler: handleNextStep } = usePromise(() => nextStepDeferred({ submit: true }));
  const { t } = useTranslation();

  return (
    <FormField
      name="leverageAttitude"
      control={form.control}
      render={({ field }) => (
        <FormItem>
          <FormLabel className="sr-only">{t('FuturesAppropriatenessAssessment.leverageAttitude.title')}</FormLabel>
          <FormControl>
            <RadioGroup
              className="!mt-0"
              defaultValue={field.value}
              variant="cards"
              aria-label={t('FuturesAppropriatenessAssessment.leverageAttitude.title')}
              onValueChange={(value) => {
                field.onChange(value);
                handleNextStep();
              }}
            >
              {FUTURES_APPROPRIATENESS_LEVERAGE_ATTITUDE_ANSWERS.map((answer) => (
                <FormItem key={answer}>
                  <RadioGroupItemCard variant="outline">
                    <FormControl>
                      <RadioGroupItem value={answer} aria-disabled={isPending} />
                    </FormControl>
                    <FormLabel contained onClick={handleNextStep}>
                      {t(`FuturesAppropriatenessAssessment.leverageAttitude.options.${answer}`)}
                    </FormLabel>
                  </RadioGroupItemCard>
                </FormItem>
              ))}
            </RadioGroup>
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};
FuturesAppropriatenessTestFieldsetLeverageAttitude.displayName = 'FuturesAppropriatenessTestFieldsetLeverageAttitude';
FuturesAppropriatenessTestFieldsetLeverageAttitude.isAutoNextStep = true;

export { FuturesAppropriatenessTestFieldsetLeverageAttitude, FuturesAppropriatenessTestFieldsetLeverageAttitudeLegend };
